import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, Spinner } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { CORE_COMPANY, MENU_BEHAVIOUR, MENU_PLACEMENT } from 'constants/index';
import { format } from 'date-fns';
import { displayErrorToast, displaySuccessToast } from 'helpers/toaster';
import { fetchAllStatements, acknowledgePaymentReceipt } from 'views/statements/actions/statementActions';
import { fetchQaList } from 'views/messages/action/action';
import ApolloClientUploadSellerService from 'ApolloClientUploadSellerService';
import { GET_SUPPORT_TICKET_LIST } from 'views/catalog/actions/supportTicketActions';
import { setSupportTicketListResult } from 'messageSlice';
import { setNonStockProductsCount } from 'dashboardSlice/dasboardStatus';
import ApolloClientCatalogService from 'ApolloClientCatalogService';
import { GET_PRODUCT_COUNT } from 'views/catalog/actions/productListActions';
import NavUserMenu from './NavUserMenu';
import NavIconMenu from './NavIconMenu';
import MainMenu from './main-menu/MainMenu';
import NavLogo from './NavLogo';
import NavMobileButtons from './NavMobileButtons';
import { menuChangeAttrMenuAnimate, menuChangeCollapseAll } from './main-menu/menuSlice';
import NavLanguageSwitcher from './NavLanguageSwitcher';
import NavCountrySwitcher from './NavCountrySwitcher';
import NoticeCenter from './notices/NoticeCenter';
import usePasswordExpiryNotice from './notices/usePasswordExpiryNotice';

const DELAY = 80;

const Nav = () => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.auth);
  // Delegated sellers get the restricted experience (no seller-wide banners).
  const isDelegated = !!currentUser?.isDelegated;

  const fetchSuportTickets = async () => {
    const { data } = await ApolloClientUploadSellerService.query({ query: GET_SUPPORT_TICKET_LIST(), variables: {} });
    const { listAllSupportTicket } = data || {};
    dispatch(setSupportTicketListResult(listAllSupportTicket));
  };
  const fetchProductsCount = async () => {
    try {
      const results = await ApolloClientCatalogService.query({
        query: GET_PRODUCT_COUNT(),
        variables: {
          filters: [
            { name: 'inventory', custom: { $elemMatch: { qty: { $lte: 0 }, country: 'ae' } } },
            { name: 'inventory_updated_at', custom: { expiredMonths: 6 } },
          ],
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
      });
      dispatch(setNonStockProductsCount(results?.data));
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    fetchAllStatements({ params: {}, dispatch });
    fetchProductsCount();

    if (currentUser?.sellerId) {
      fetchQaList({
        params: {
          start: 0,
          limit: 100,
          search_qry: '',
          seller_id: currentUser?.sellerId,
          date_from: '',
          date_to: '',
          orderBy: 'id',
        },
        dispatch,
      });
    }
    setInterval(() => {
      fetchSuportTickets();
    }, 5 * 60 * 60 * 1000);
    fetchSuportTickets();
  }, []);
  const { list } = useSelector((state) => state.statements);
  const { noStockOutdatedCount } = useSelector((state) => state.dashboardStatus);
  const { navClasses, placementStatus, behaviourStatus, attrMobile, menuPadding } = useSelector((state) => state.menu);
  const mouseActionTimer = useRef(null);

  // Vertical menu semihidden state showing
  // Only works when the vertical menu is active and mobile menu closed
  const onMouseEnterDelay = () => {
    if (placementStatus.placementHtmlData === MENU_PLACEMENT.Vertical && behaviourStatus.behaviourHtmlData === MENU_BEHAVIOUR.Unpinned && attrMobile !== true) {
      dispatch(menuChangeCollapseAll(false));
      dispatch(menuChangeAttrMenuAnimate('show'));
    }
  };

  // Delayed one that hides or shows the menu. It's required to prevent collapse animation getting stucked
  const onMouseEnter = () => {
    if (mouseActionTimer.current) clearTimeout(mouseActionTimer.current);

    mouseActionTimer.current = setTimeout(() => {
      onMouseEnterDelay();
    }, DELAY);
  };

  // Vertical menu semihidden state hiding
  // Only works when the vertical menu is active and mobile menu closed
  const onMouseLeaveDelay = () => {
    if (placementStatus.placementHtmlData === MENU_PLACEMENT.Vertical && behaviourStatus.behaviourHtmlData === MENU_BEHAVIOUR.Unpinned && attrMobile !== true) {
      dispatch(menuChangeCollapseAll(true));
      dispatch(menuChangeAttrMenuAnimate('hidden'));
    }
  };

  const onMouseLeave = () => {
    if (mouseActionTimer.current) clearTimeout(mouseActionTimer.current);
    mouseActionTimer.current = setTimeout(() => {
      onMouseLeaveDelay();
    }, DELAY);
  };
  const date = new Date();
  const statmentStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
  const statementEndDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const paymentDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const soaStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
  const soaEndDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  if (date.getDate() > 15) {
    statmentStartDate.setDate(16);
    soaEndDate.setDate(15);
    paymentDate.setDate(15);
    paymentDate.setMonth(paymentDate.getMonth() + 1);
  } else {
    soaStartDate.setDate(16);
    soaStartDate.setMonth(date.getMonth() - 1);
    soaEndDate.setMonth(date.getMonth() - 1);
    statementEndDate.setDate(15);
  }
  let showAlert = false;
  if (statementEndDate.getDate() - date.getDate() < 3) {
    showAlert = true;
  }
  const showSOAlert = false;
  const statementId = `${format(soaStartDate, 'dd-MM-yyyy')}_${format(soaEndDate, 'dd-MM-yyyy')}?generate_soa`;

  const pendingAcknowledgment = list?.find((statement) => statement.payment_processed_on && !statement.payment_received_on);

  const [waitingResponse, setWaitingResponse] = useState(false);

  const handleReceiptAcknowledgement = async () => {
    try {
      setWaitingResponse(true);
      const results = await acknowledgePaymentReceipt({
        params: {
          statementPeriodId: pendingAcknowledgment.statement_period_id,
          // Pin to the one statement: a dual-country seller has an AE and an
          // OM statement sharing the same period id.
          statementId: pendingAcknowledgment.statement_id || undefined,
        },
      });
      setWaitingResponse(false);
      if (results?.acknowledgePaymentReceipt?.success) {
        fetchAllStatements({ params: {}, dispatch });
        displaySuccessToast(`Payment Receipt acknowledged.`);
      } else {
        displayErrorToast(`Something went wrong while updating ${pendingAcknowledgment.statement_period_id}`);
      }
    } catch (e) {
      console.error(e.message);
    }
  };

  const passwordExpiryNotice = usePasswordExpiryNotice();
  // Same-severity notices keep this order, so the password notice leads.
  const notices = [
    passwordExpiryNotice,
    !isDelegated &&
      showAlert && {
        id: 'invoice-deadline',
        severity: 'critical',
        icon: 'invoice',
        title: 'Invoice submission deadline is approaching',
        message: (
          <>
            Submit your invoices for {format(statmentStartDate, 'do MMM')} – {format(statementEndDate, 'do MMM, yyyy')} by{' '}
            <b>{format(statementEndDate, 'do MMM yyyy')}, 11:59 PM</b>. Invoices raised after this won&apos;t be included in the payment on{' '}
            {format(paymentDate, 'do MMM, yyyy')}.
          </>
        ),
      },
    !isDelegated &&
      showSOAlert && {
        id: 'soa-due',
        severity: 'action',
        icon: 'file-text',
        title: 'Generate your statement of account',
        message: `Payment processing for ${format(soaStartDate, 'do MMM')} – ${format(soaEndDate, 'do MMM, yyyy')} is due. Generate the SOA to start it.`,
        action: (
          <NavLink className="btn btn-sm btn-outline-primary" to={`/statements/transactions/detail/${statementId}`}>
            Generate SOA
          </NavLink>
        ),
      },
    !isDelegated &&
      pendingAcknowledgment && {
        id: 'payment-acknowledgement',
        severity: 'action',
        icon: 'wallet',
        title: 'Please confirm you received your payment',
        message: (
          <>
            <b>
              {pendingAcknowledgment.currency || 'AED'} {pendingAcknowledgment.total_payout}
            </b>{' '}
            for {pendingAcknowledgment.statement_period_from_date} – {pendingAcknowledgment.statement_period_end_date} has been paid. Acknowledge it so your
            next statement can be processed.
          </>
        ),
        action: (
          <Button size="sm" variant="outline-primary" onClick={handleReceiptAcknowledgement} disabled={waitingResponse}>
            {waitingResponse && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />}
            Acknowledge payment
          </Button>
        ),
      },
    !isDelegated &&
      noStockOutdatedCount > 1 && {
        id: 'out-of-stock',
        severity: 'warning',
        icon: 'box',
        title: `${noStockOutdatedCount} products have been out of stock for over 6 months`,
        message: 'Update their inventory soon to avoid these products being unlisted automatically.',
        action: (
          <NavLink className="btn btn-sm btn-outline-primary" to="/catalog/inventory?download=no-stock">
            Download list
          </NavLink>
        ),
      },
  ].filter(Boolean);

  return (
    <>
      <div id="nav" className={classNames('nav-container d-flex', navClasses)} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <div
          className="nav-content d-flex"
          style={placementStatus.placementHtmlData === MENU_PLACEMENT.Horizontal && menuPadding ? { paddingRight: menuPadding } : {}}
        >
          <NavLogo />
          <NavLanguageSwitcher />
          <If condition={CORE_COMPANY === currentUser?.sellerId}>
            <NavCountrySwitcher />
          </If>
          <NavUserMenu />
          <NavIconMenu />
          <MainMenu />
          <NavMobileButtons />
        </div>
        <div className="nav-shadow" />
      </div>
      <NoticeCenter notices={notices} />
    </>
  );
};
export default React.memo(Nav);
